import {
    ColumnFilter,
    ColumnSort,
    EnumFilterType,
    Filter,
    Order,
    ReducerActionType,
    TableFilters,
    TableReducerAction,
    TableState,
} from './types';

const cleanTableState = <T, F extends TableFilters<T>>(state: TableState<T, F>) => {
    const getFilters = () =>
        Object.keys(state.filters).reduce((acc: TableFilters<T>, current) => {
            const columnId = current as keyof T;
            const activeFilters = [...(state.filters[columnId] || [])].filter((filterItem: ColumnFilter) => {
                if (Array.isArray(filterItem?.value)) {
                    return !!filterItem?.value.length;
                }
                if (typeof filterItem?.value === 'boolean') {
                    return true;
                }

                return !!filterItem?.value;
            });

            if (activeFilters?.length) {
                acc[columnId] = state.filters[columnId];
            }

            return acc;
        }, {});

    const getGlobalFilters = () => (state.globalFilters?.search === '' ? {} : state.globalFilters);

    return {
        ...state,
        filters: getFilters(),
        globalFilters: getGlobalFilters(),
    } as TableState<T, F>;
};

export const tableStateReducer = <T, F extends TableFilters<T>>(
    state: TableState<T, F>,
    { type, payload = {} }: TableReducerAction<T, F>
) => {
    let nextState = {};

    switch (type) {
        case ReducerActionType.GlobalSearch: {
            nextState = {
                ...state,
                globalFilters: payload,
                pagination: {
                    ...state.pagination,
                    pageIndex: 0,
                },
            };
            break;
        }
        case ReducerActionType.Pagination: {
            nextState = {
                ...state,
                pagination: {
                    ...state.pagination,
                    ...payload,
                },
            };
            break;
        }
        case ReducerActionType.RemoveAllFilters: {
            nextState = {
                ...state,
                filters: {},
                pagination: {
                    ...state.pagination,
                    pageIndex: 0,
                },
            };
            break;
        }
        case ReducerActionType.RemoveFilter: {
            if (!payload || !('columnFilter' in payload)) {
                break;
            }

            const { columnFilter, columnName } = payload;

            const nextFilters = [...(state.filters[columnName] || [])].filter(
                (filterItem: ColumnFilter) => filterItem?.value !== columnFilter?.value
            );

            nextState = {
                ...state,
                filters: {
                    ...state.filters,
                    [columnName]: nextFilters,
                },
                pagination: {
                    ...state.pagination,
                    pageIndex: 0,
                },
            };
            break;
        }
        case ReducerActionType.RemoveMultiSelectFilter: {
            if (!payload || !('columnFilter' in payload)) {
                break;
            }

            const { columnFilter, selectedValue, columnName } = payload;

            const nextValues = (columnFilter as Filter<EnumFilterType, string[]>)?.value.filter(
                (value) => value !== selectedValue
            );

            nextState = {
                ...state,
                filters: {
                    ...state.filters,
                    [columnName]: [
                        {
                            filterType: columnFilter?.filterType,
                            value: nextValues,
                        },
                    ],
                },
                pagination: {
                    ...state.pagination,
                    pageIndex: 0,
                },
            };
            break;
        }
        case ReducerActionType.SetColumnFilter: {
            if (!payload || !('columnFilter' in payload)) {
                break;
            }

            const { columnFilter, columnName } = payload;

            nextState = {
                ...state,
                filters: {
                    ...state.filters,
                    [columnName]: [columnFilter],
                },
                pagination: {
                    ...state.pagination,
                    pageIndex: 0,
                },
            };
            break;
        }
        case ReducerActionType.Sort: {
            if (!payload || !('currentOrder' in payload)) {
                break;
            }

            const { currentOrder, columnName } = payload;

            let order;
            if (currentOrder === undefined) {
                order = Order.Ascending;
            } else if (currentOrder === Order.Ascending) {
                order = Order.Descending;
            } else {
                order = undefined;
            }

            const sort =
                order !== undefined
                    ? ({
                          [columnName]: {
                              order,
                              priority: 0,
                          },
                      } as { [key in keyof T]: ColumnSort })
                    : {};

            nextState = {
                ...state,
                sort,
                pagination: {
                    ...state.pagination,
                    pageIndex: 0,
                },
            };
            break;
        }
    }
    return cleanTableState(nextState as TableState<T, F>);
};
